/**
 * `<AmbientErrorNotice/>` — a small, calm one-line indicator for the
 * ambient pill/ticker sections of the persistent right panel (#781).
 *
 * **Why this exists.** The panel sections (`<LiveMoodSection/>`,
 * `<SubmissionProgressSection/>`, `<TeamAuditTickerSection/>`, …) used
 * to `return null` on *any* `query.isError`. That made a real
 * 401/403/5xx indistinguishable from "no data yet" — the user just saw
 * a missing section. This silent-hide masked the #742 notes-401 and is
 * exactly the failure mode the #266 auth migration could re-introduce.
 *
 * The principle (per #781): an **expected** empty / no-data / 404 keeps
 * hiding (`return null`); an **unexpected** error (401/403/5xx, or a
 * network/parse failure) renders this muted notice instead of vanishing.
 *
 * **Brand discipline** (`feedback_brand_pillars.md` +
 * `feedback_ui_section_focal_component.md`): these are ambient surfaces,
 * so the notice stays calm and subtle — one muted line, no alarm tone,
 * no card-within-card. It is supporting chrome, never a focal element.
 * The retry affordance is a quiet inline text button.
 */
import { RefreshCw } from "lucide-react";

import { ApiError } from "../api/client";

/**
 * Is this query error *unexpected* (worth surfacing) rather than an
 * ordinary "no data here" signal we should keep hiding?
 *
 * - `404` → expected (the resource genuinely isn't there — a gated-off
 *   component, an unsolved cycle, a member with no audit yet). Keep
 *   hiding by returning `false`.
 * - `ApiError` with `401 | 403 | >= 500` → unexpected. A broken session
 *   or a server fault is precisely what must not be swallowed.
 * - Any other `ApiError` 4xx (e.g. 400/409/422 on a plain GET) → also
 *   treated as unexpected; a panel GET shouldn't hit these.
 * - A non-`ApiError` (network drop, JSON parse fail) → unexpected; the
 *   user couldn't load and should be told, calmly.
 */
export function isUnexpectedApiError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status !== 404;
  }
  // Network / parse failures still mean "couldn't load" — surface them.
  return error != null;
}

export interface AmbientErrorNoticeProps {
  /** Re-runs the underlying query (typically `query.refetch`). */
  onRetry: () => void;
  /** Accessible label for the section wrapper (e.g. "Live mood"). */
  label?: string | undefined;
  /** Test hook so each section can assert its own notice rendered. */
  testId?: string | undefined;
}

export function AmbientErrorNotice({ onRetry, label, testId }: AmbientErrorNoticeProps) {
  return (
    <section
      data-testid={testId ?? "ambient-error-notice"}
      // #2172 — rises in when it mounts.
      className="motion-state-in flex items-center justify-between gap-2 px-1 py-2"
      aria-label={label ? `${label} — couldn't load` : "Section couldn't load"}
    >
      <span className="text-xs text-fg-muted">Couldn&rsquo;t load</span>
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
        data-testid={testId ? `${testId}-retry` : "ambient-error-notice-retry"}
      >
        <RefreshCw className="h-3 w-3" aria-hidden="true" />
        Retry
      </button>
    </section>
  );
}
